import { useEffect, useRef, useCallback } from 'react';

/** 진행자 웹소켓: onSeek·재생/일시정지 연쇄 시 송신을 한 번으로 묶음 */
const HOST_SYNC_DEBOUNCE_MS = 250;

/** 참가자: 이미 거의 같은 위치면 seek 생략 */
const PARTICIPANT_NEAR_MATCH = 0.02;
const PARTICIPANT_SAME_TARGET_EPS = 1e-6;

/**
 * 플레이어 ref에서 재생 비율(0~1) 조회. 실패 시 null.
 */
export function GetPlayedFractionFromPlayer(videoRef) {
  const p = videoRef?.current;
  if (!p) return null;
  try {
    const duration = p.getDuration();
    const time = p.getCurrentTime();
    if (!duration || duration <= 0) return null;
    return time / duration;
  } catch (_) {
    return null;
  }
}

/**
 * 진행자: 웹소켓 동기화는 ScheduleEmitHostVideoSync만 사용(내부 디바운스 후 송신).
 */
export function useHostVideoWebSocketSync({
  isHost,
  videoRef,
  played,
  videoPlaying,
  ActiveButton,
}) {
  const videoPlayingRef = useRef(videoPlaying);
  useEffect(() => {
    videoPlayingRef.current = videoPlaying;
  }, [videoPlaying]);

  const timerRef = useRef(null);
  const sendRef = useRef(() => { });

  const SendHostVideoSync = useCallback(() => {
    if (!isHost) return;
    const fromPlayer = GetPlayedFractionFromPlayer(videoRef);
    const fraction = fromPlayer != null ? fromPlayer : played;
    ActiveButton({
      type: 'videoPlaying',
      videoPlaying: videoPlayingRef.current,
      played: fraction,
    });
  }, [isHost, played, videoRef, ActiveButton]);

  sendRef.current = SendHostVideoSync;

  const ScheduleEmitHostVideoSync = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      sendRef.current();
    }, HOST_SYNC_DEBOUNCE_MS);
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    []
  );

  return { ScheduleEmitHostVideoSync };
}

/**
 * 참가자: 진행자가 보낸 비율에 맞춰 seek.
 */
export function useParticipantVideoSeekSync({
  isHost,
  videoRef,
  videoPlayedFraction,
}) {
  const lastAppliedRef = useRef(null);

  useEffect(() => {
    if (isHost) return;
    if (!videoRef.current) return;

    const target = videoPlayedFraction;
    if (typeof target !== 'number' || Number.isNaN(target)) return;

    const localFraction = GetPlayedFractionFromPlayer(videoRef);
    if (localFraction == null) return;

    if (
      lastAppliedRef.current != null &&
      Math.abs(lastAppliedRef.current - target) < PARTICIPANT_SAME_TARGET_EPS
    ) {
      return;
    }
    if (Math.abs(localFraction - target) < PARTICIPANT_NEAR_MATCH) {
      lastAppliedRef.current = target;
      return;
    }
    lastAppliedRef.current = target;
    videoRef.current.seekTo(target, 'fraction');
  }, [isHost, videoRef, videoPlayedFraction]);
}
